/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { SubscriptionProto } from "@noom/noom-contracts/noom_contracts/billing/subscription/v2/subscription";
import { getActiveProgramSubscription } from "src/utils/api/subscriptions";
import { getProduct } from "src/utils/api/productCatalog";
import { captureException } from "src/utils/error";
import { createAppAsyncThunk } from "../async-thunk";
import getStore from "../store";

export interface BillingDataState {
  /**
   * The user's active program subscription from the billing v2Proto API.
   * `undefined` means not loaded yet; `null` means no active subscription found.
   */
  activeSubscription?: SubscriptionProto | null;
  /**
   * Target catalog product name for an end-of-billing-cycle switch.
   * `undefined` means not loaded yet; `null` means no pending switch.
   */
  pendingSwitchProductName?: string | null;
}

const billingDataSlice = createSlice({
  name: "billingData",
  initialState: {} as BillingDataState,
  reducers: {
    setActiveSubscription(
      state,
      action: PayloadAction<SubscriptionProto | null>
    ) {
      state.activeSubscription = action.payload;
    },
    setPendingSwitchProductName(state, action: PayloadAction<string | null>) {
      state.pendingSwitchProductName = action.payload;
    },
    resetBillingData: () => ({} as BillingDataState),
  },
});

export const {
  setActiveSubscription,
  setPendingSwitchProductName,
  resetBillingData,
} = billingDataSlice.actions;

export default billingDataSlice;

let activeSubscriptionPromise: Promise<void> | null = null;
let pendingSwitchPromise: Promise<void> | null = null;

/**
 * Incremented on every {@link resetBillingDataCache}. In-flight loaders capture
 * the value at start and skip dispatches after await if it has changed, so
 * stale network results cannot repopulate Redux after a reset.
 */
let billingDataInvalidationGeneration = 0;

/**
 * Clears both Redux billing state and module-level promise caches so the next
 * load call fetches fresh data. Call at flow re-entry points (e.g.
 * onStartCancellationSurvey) to avoid stale data on back-to-back flows.
 *
 * Also bumps an invalidation generation so any async work started before this
 * call will not dispatch when it finishes.
 */
export function resetBillingDataCache() {
  billingDataInvalidationGeneration += 1;
  activeSubscriptionPromise = null;
  pendingSwitchPromise = null;
  getStore().dispatch(resetBillingData());
}

export const loadActiveSubscription = createAppAsyncThunk<void, void>(
  "billingData/loadActiveSubscription",
  async (_, thunkAPI) => {
    const { activeSubscription } = thunkAPI.getState().billingData;
    if (activeSubscription !== undefined) {
      return;
    }

    if (!activeSubscriptionPromise) {
      const subscriptionLoadPromise = (async () => {
        const generationAtStart = billingDataInvalidationGeneration;
        try {
          const subscription = await getActiveProgramSubscription();
          if (generationAtStart !== billingDataInvalidationGeneration) {
            return;
          }
          thunkAPI.dispatch(setActiveSubscription(subscription ?? null));
        } catch (error) {
          captureException(error, "loadActiveSubscription");
        } finally {
          if (activeSubscriptionPromise === subscriptionLoadPromise) {
            activeSubscriptionPromise = null;
          }
        }
      })();
      activeSubscriptionPromise = subscriptionLoadPromise;
    }

    await activeSubscriptionPromise;
  }
);

/**
 * Fetches the active program subscription's pending end-of-billing-cycle switch
 * target product name. Cached: repeated calls share a single in-flight request
 * and skip entirely once Redux is hydrated.
 */
export const loadPendingSwitchData = createAppAsyncThunk<void, void>(
  "billingData/loadPendingSwitchData",
  async (_, thunkAPI) => {
    const { pendingSwitchProductName } = thunkAPI.getState().billingData;
    if (pendingSwitchProductName !== undefined) {
      return;
    }

    if (!pendingSwitchPromise) {
      const switchLoadPromise = (async () => {
        const generationAtStart = billingDataInvalidationGeneration;
        try {
          await thunkAPI.dispatch(loadActiveSubscription());
          if (generationAtStart !== billingDataInvalidationGeneration) {
            return;
          }

          const { activeSubscription } = thunkAPI.getState().billingData;

          if (activeSubscription === undefined) {
            return;
          }

          const lineItems =
            activeSubscription?.endOfBillingCycleSwitchDetails?.lineItems;

          if (!lineItems?.length || !lineItems[0].saleItemId) {
            thunkAPI.dispatch(setPendingSwitchProductName(null));
            return;
          }

          const product = await getProduct(lineItems[0].saleItemId);
          if (generationAtStart !== billingDataInvalidationGeneration) {
            return;
          }

          const productName =
            product?.subscriptionSaleItem?.productName ??
            product?.trialSaleItem?.productName ??
            null;

          thunkAPI.dispatch(setPendingSwitchProductName(productName));
        } catch (error) {
          captureException(error, "loadPendingSwitchData");
        } finally {
          if (pendingSwitchPromise === switchLoadPromise) {
            pendingSwitchPromise = null;
          }
        }
      })();
      pendingSwitchPromise = switchLoadPromise;
    }

    await pendingSwitchPromise;
  }
);
